import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import classNames from "classnames";
import breakpoints from "@/constants/breakpoinst";
import { startOfDay } from "date-fns";

import {
  Button as HeadlessButton,
  Dialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { Nav } from "@/components/navigation/Navbar";
import Button from "@/components/actions/Button";
import {
  ChevronDownIcon,
  XIcon,
  AdjustmentsIcon,
  ArrowUpIcon,
} from "@heroicons/react/solid";
import { FilterIcon } from "@heroicons/react/outline";
import { Listbox, Transition } from "@headlessui/react";
import * as Text from "@/components/text";
import {
  getEventsByMonths,
  getProyectionsByMonths,
} from "@/actions/eventsActions";
import EventCard from "@/components/event/EventCard";
import useWindowSize from "@/hooks/useWindowSize";
import Slider from "@/components/slider";
import { localeTexts } from "@/utils/locale";
import { getStrapiImage } from "@/utils/strapiFunctions";

export default function Calendar({ locale }) {
  const months = localeTexts[locale].calendar.months;
  const mexicanStates = localeTexts[locale].calendar.mexicanStates;
  const eventTypes = localeTexts[locale].calendar.eventTypes;

  const [monthEvents, setMonthEvents] = React.useState([]);
  const [monthProyections, setMonthProyections] = React.useState([]);
  const [types, setTypes] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const router = useRouter();
  const navRef = useRef(null);

  const [allEvents, setAllEvents] = useState([]);

  useEffect(() => {
    const newProjections = monthProyections.map((projection) => {
      return {
        ...projection,
        orderDate:
          projection?.type === "live"
            ? projection.attributes?.live_screenings?.[0]?.date
            : projection?.type === "online"
            ? projection?.attributes?.online_screenings?.[0]?.start_date
            : projection?.attributes?.tv_screenings?.[0]?.date,
        cardType: "projection",
      };
    });

    const newEvents = monthEvents.map((event) => {
      return {
        ...event,
        orderDate: event.attributes.date_time,
        cardType: "event",
      };
    });

    // merge events and projections and order them by date
    const allEvents = [...newEvents, ...newProjections].sort((a, b) => {
      return new Date(a.orderDate) - new Date(b.orderDate);
    });

    setAllEvents(allEvents);
  }, [monthProyections, monthEvents]);

  const [month, setMonth] = React.useState(new Date().getMonth());
  const [filters, setFilters] = React.useState({
    $or: [],
  });

  function tagProyections(proyections, tag) {
    return proyections.map((projection) => ({
      ...projection,
      type: tag,
    }));
  }

  const fetchProjections = async (projectionType) => {
    try {
      const response = await getProyectionsByMonths(
        month,
        filters,
        projectionType
      );
      return response?.data?.data;
    } catch (error) {
      console.error(`Error fetching projections for ${projectionType}:`, error);
      return [];
    }
  };

  const fetchP = async () => {
    try {
      const [onlineProjections, liveProjections, tvProjections] =
        await Promise.all([
          fetchProjections("online"),
          fetchProjections("live"),
          fetchProjections("tv"),
        ]);
      const onlineProyectionstegged = tagProyections(
        onlineProjections,
        "online"
      );
      const liveProyectionstegged = tagProyections(liveProjections, "live");
      const tvProjectionstegged = tagProyections(tvProjections, "tv");
      setMonthProyections([
        ...onlineProyectionstegged,
        ...liveProyectionstegged,
        ...tvProjectionstegged,
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const { width } = useWindowSize();

  React.useEffect(() => {
    getEventsByMonths(month, filters).then((response) => {
      setMonthEvents(response.data.data);
    });
    if (types.length > 0) {
      setMonthProyections([]);
    } else {
      fetchP();
    }
  }, [month, filters]);

  React.useEffect(() => {
    if (types) {
      const actualOr = filters.$or;

      const fiteredFt = actualOr.filter((filter) => {
        return !filter.event_type;
      });

      const newTypes = types.map((type) => {
        return {
          event_type: type,
        };
      });

      const newFilters = [...fiteredFt, ...newTypes];

      setFilters({
        $or: newFilters,
      });
    }
  }, [types]);

  React.useEffect(() => {
    if (states) {
      const actualOr = filters.$or;

      const fiteredFt = actualOr.filter((filter) => {
        return !filter?.sede?.states?.$containsi;
      });

      const newStates = states.map((state) => {
        if (state === "Estado de México") {
          state = "Mexico";
        }
        return {
          sede: {
            states: {
              $eq: state
                .normalize("NFD")
                .replace(/[̀-ͯ]/g, "")
                .toUpperCase(),
            },
          },
        };
      });

      const newFilters = [...fiteredFt, ...newStates];

      setFilters({
        $or: newFilters,
      });
    } else {
      // remove states filter
      setFilters({
        $or: fiteredFt,
      });
    }
  }, [states]);

  function clearFilters() {
    setTypes([]);
    setStates([]);
  }

  const todayFilteredEvents = allEvents.filter((event) => {
    if (event.cardType === "event") {
      return (
        startOfDay(new Date(event.orderDate)).getTime() ===
        startOfDay(new Date()).getTime()
      );
    } else {
      // it's a projection
      if (event.type === "live") {
        return (
          startOfDay(new Date(event.orderDate)).getTime() ===
          startOfDay(new Date()).getTime()
        );
      } else if (event.type === "online") {
        const screening = event.attributes?.online_screenings?.[0];
        if (!screening) return false;
        return (
          startOfDay(new Date(screening.start_date)).getTime() <=
            startOfDay(new Date()).getTime() &&
          startOfDay(new Date(screening.end_date)).getTime() >=
            startOfDay(new Date()).getTime()
        );
      } else if (event.type === "tv") {
        const screening = event.attributes?.tv_screenings?.[0];
        if (!screening) return false;
        return (
          startOfDay(new Date(screening.date)).getTime() <=
            startOfDay(new Date()).getTime() &&
          startOfDay(new Date(screening.end_date)).getTime() >=
            startOfDay(new Date()).getTime()
        );
      }
    }
    return false;
  });

  return (
    <div>
      <div ref={navRef} className="amb-container bg-white">
        <Nav theme="light" />
      </div>

      <div className="sticky top-0 z-50 ">
        <div className="bg-white py-3 ">
          <div className="amb-container">
            <ul className="flex gap-5">
              <li className="flex space-x-2">
                <Text.Link
                  label={localeTexts[locale].atras}
                  onClick={() => router.back()}
                  size="md"
                  className="flex items-center align-middle text-neutral-900"
                ></Text.Link>
              </li>
              <li className="flex after:ml-5 after:content-['/'] ">
                <Link href="/" legacyBehavior>
                  <Text.Link size="md" label={localeTexts[locale].breadcrumbs.Home}></Text.Link>
                </Link>
              </li>
              <li>
                <Text.Body
                  className="line-clamp-1 overflow-hidden text-ellipsis font-light text-neutral-500"
                  size="lg"
                >
                  {localeTexts[locale].calendar.calendar}
                </Text.Body>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-neutral-50 py-2 ">
        <div className="amb-container mb-8 mt-8 space-y-5 ">
          <Text.Headline as="p">Hoy</Text.Headline>
          <div className="hidden gap-4 md:grid md:grid-cols-3 lg:grid-cols-4">
            {todayFilteredEvents.map((event) => {
              if (event.cardType === "event") {
                return (
                  <EventCard
                    key={event.id}
                    size={width < breakpoints.md ? "lg" : "md"}
                    event_type={event.attributes.event_type}
                    event_title={event.attributes.event_name}
                    date_time={event.attributes.date_time}
                    display_hour={event.attributes.display_hour}
                    event_venue={
                      event.attributes?.sede?.data?.attributes?.name || ""
                    }
                    event_state={
                      event?.attributes?.sede?.data?.attributes?.states ||
                      "Sin Estado"
                    }
                    event_image_url={
                      getStrapiImage(event?.attributes?.event_image, "small")
                    }
                    event_url={`/calendario/${event.id}`}
                  />
                );
              } else {
                let projection = event;
                if (projection?.attributes?.peliculas.data?.[0]) {
                  return (
                    <EventCard
                      key={projection.id}
                      size={width < breakpoints.md ? "lg" : "md"}
                      event_type={
                        projection.type === "live"
                          ? "Proyección"
                          : projection.type === "online"
                          ? "Gira en línea"
                          : "Proyección en tv"
                      }
                      event_title={projection.attributes.title}
                      date_time={
                        projection?.type === "live"
                          ? projection.attributes?.live_screenings?.[0]?.date
                          : projection?.type === "online"
                          ? {
                              from: projection?.attributes?.online_screenings?.[0]
                                ?.start_date,
                              to: projection?.attributes?.online_screenings?.[0]
                                ?.end_date,
                            }
                          : {
                              from: projection?.attributes?.tv_screenings?.[0]
                                ?.date,
                              to: projection?.attributes?.tv_screenings?.[0]
                                ?.end_date,
                            }
                      }
                      display_hour={
                        projection?.type === "live"
                          ? projection.attributes?.live_screenings?.[0]
                              ?.display_hour
                          : ""
                      }
                      event_venue={
                        projection.attributes?.sede?.data?.attributes?.name || ""
                      }
                      event_state={
                        projection?.attributes?.sede?.data?.attributes?.states ||
                        "Sin Estado"
                      }
                      event_image_url={
                        getStrapiImage(
                          projection?.attributes?.peliculas.data?.[0]
                            ?.attributes?.film_cover_image,
                          "small"
                        )
                      }
                      event_url={`/catalogo/${projection?.attributes?.peliculas.data?.[0].attributes?.filmSlug}`}
                    />
                  );
                }
              }
            })}
          </div>

          <div className="md:hidden">
            <Slider
              slides={todayFilteredEvents.map((event) => {
                if (event.cardType === "event") {
                  return (
                    <EventCard
                      key={event.id}
                      size={width < breakpoints.md ? "lg" : "md"}
                      event_type={event.attributes.event_type}
                      event_title={event.attributes.event_name}
                      date_time={event.attributes.date_time}
                      display_hour={event.attributes.display_hour}
                      event_venue={
                        event.attributes?.sede?.data?.attributes?.name || ""
                      }
                      event_state={
                        event?.attributes?.sede?.data?.attributes?.states ||
                        "Sin Estado"
                      }
                      event_image_url={
                        getStrapiImage(event?.attributes?.event_image, "medium")
                      }
                      event_url={`/calendario/${event.id}`}
                    />
                  );
                } else {
                  let projection = event;
                  if (projection?.attributes?.peliculas.data?.[0]) {
                    return (
                      <EventCard
                        key={projection.id}
                        size={width < breakpoints.md ? "lg" : "md"}
                        event_type={
                          projection.type === "live"
                            ? "Proyección"
                            : projection.type === "online"
                            ? "Gira en línea"
                            : "Proyección en tv"
                        }
                        event_title={projection.attributes.title}
                        date_time={
                          projection?.type === "live"
                            ? projection.attributes?.live_screenings?.[0]?.date
                            : projection?.type === "online"
                            ? {
                                from: projection?.attributes
                                  ?.online_screenings?.[0]?.start_date,
                                to: projection?.attributes?.online_screenings?.[0]
                                  ?.end_date,
                              }
                            : {
                                from: projection?.attributes?.tv_screenings?.[0]
                                  ?.date,
                                to: projection?.attributes?.tv_screenings?.[0]
                                  ?.end_date,
                              }
                        }
                        display_hour={
                          projection?.type === "live"
                            ? projection.attributes?.live_screenings?.[0]
                                ?.display_hour
                            : ""
                        }
                        event_venue={
                          projection.attributes?.sede?.data?.attributes?.name ||
                          ""
                        }
                        event_state={
                          projection?.attributes?.sede?.data?.attributes
                            ?.states || "Sin Estado"
                        }
                        event_image_url={
                          getStrapiImage(
                            projection?.attributes?.peliculas.data?.[0]
                              ?.attributes?.film_cover_image,
                            "small"
                          )
                        }
                        event_url={`/catalogo/${projection?.attributes?.peliculas.data?.[0].attributes?.filmSlug}`}
                      />
                    );
                  }
                }
              })}
            ></Slider>
          </div>
        </div>
      </div>
      <div className="amb-container mt-10 space-y-5">
        <div className="flex items-center">
          <div className="flex md:flex-1">
            <Text.Headline fontWeight="light" as="p">
              {localeTexts[locale].calendar.eventsIn}&nbsp;
            </Text.Headline>
            <Listbox
              as="div"
              className="relative"
              value={month}
              onChange={setMonth}
            >
              {({ open }) => (
                <>
                  <Listbox.Button>
                    <Text.Headline
                      className={classNames(
                        open ? "bg-black text-white" : "hover:bg-neutral-50",
                        "px-2 py-1"
                      )}
                    >
                      {months[month].name}&nbsp;
                      <ChevronDownIcon className="inline-block h-8 w-8"></ChevronDownIcon>
                    </Text.Headline>
                  </Listbox.Button>
                  <Listbox.Options
                    as="div"
                    className="absolute right-0  z-50 grid w-[300px] grid-cols-3  border border-black bg-white md:left-0 md:w-[250px]"
                  >
                    {months.map((m) => (
                      <Listbox.Option
                        as="div"
                        className="flex cursor-pointer items-center align-middle "
                        key={m.id}
                        value={m.id}
                        disabled={m.id < new Date().getMonth() ? true : false}
                      >
                        {({ active, selected }) => (
                          <p
                            className={
                              m.id < new Date().getMonth()
                                ? "w-full cursor-default px-2 py-2 text-center text-neutral-300"
                                : classNames(
                                    selected
                                      ? "bg-black text-white"
                                      : "text-neutral-300 ",
                                    "w-full cursor-pointer px-2 py-2 text-center hover:bg-neutral-50 hover:text-black"
                                  )
                            }
                          >
                            {m.short}
                          </p>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </>
              )}
            </Listbox>
          </div>
          <div className="flex-grow"></div>
          <div className="z-40 items-center hidden gap-3 lg:flex">
            <Text.Body size="xl">{localeTexts[locale].calendar.filterBy}</Text.Body>

            <Listbox
              as="div"
              className="relative"
              value={states}
              onChange={setStates}
              multiple
            >
              {({ open }) => (
                <>
                  <Listbox.Button
                    className={classNames(
                      open ? "bg-neutral-800 text-white" : "",
                      "bg-neutral-50 px-3 py-2 text-neutral-900 hover:bg-neutral-800 hover:text-white"
                    )}
                  >
                    {localeTexts[locale].calendar.states}&nbsp;
                    <ChevronDownIcon className="inline w-6 h-6"></ChevronDownIcon>
                  </Listbox.Button>
                  <Listbox.Options className="absolute max-h-[250px]  w-[250px] overflow-scroll border border-black bg-white">
                    {mexicanStates.map((t, i) => (
                      <Listbox.Option
                        as="div"
                        className="flex items-center gap-4 sm:px-4 sm:py-1"
                        key={t.id}
                        value={t.name}
                      >
                        <input
                          className="h-4 w-4 accent-black"
                          id={t.name}
                          name={t.id}
                          value={t.name}
                          checked={states.includes(t.name)}
                          type="checkbox"
                        />
                        <label
                          className="w-full border-b-[1px] border-b-neutral-200 text-sm  text-neutral-1000 sm:py-2"
                          htmlFor={t.id}
                        >
                          {t.name}
                        </label>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </>
              )}
            </Listbox>

            <Listbox
              as="div"
              className="relative"
              value={types}
              onChange={setTypes}
              multiple
            >
              {({ open }) => (
                <>
                  <Listbox.Button
                    className={classNames(
                      open ? "bg-neutral-800 text-white" : "",
                      "bg-neutral-50 px-3 py-2 text-neutral-900 hover:bg-neutral-800 hover:text-white"
                    )}
                  >
                    {localeTexts[locale].calendar.eventType}&nbsp;
                    <ChevronDownIcon className="inline w-6 h-6"></ChevronDownIcon>
                  </Listbox.Button>
                  <Listbox.Options className="absolute right-0 w-[250px] border border-black bg-white">
                    {eventTypes.map((t, i) => (
                      <Listbox.Option
                        as="div"
                        className="flex items-center gap-4 sm:px-4 sm:py-1"
                        key={t}
                        value={t}
                      >
                        <input
                          className="h-4 w-4 accent-black"
                          id={t}
                          name={i}
                          value={t}
                          checked={types.includes(t)}
                          type="checkbox"
                        />
                        <label
                          className="w-full border-b-[1px] border-b-neutral-200 text-sm  text-neutral-1000 sm:py-2"
                          htmlFor={i}
                        >
                          {t}
                        </label>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </>
              )}
            </Listbox>
          </div>
          <div className="lg:hidden">
            <FilterButton
              types={types}
              states={states}
              setStates={setStates}
              setTypes={setTypes}
              locale={locale}
              mexicanStates={mexicanStates}
              eventTypes={eventTypes}
            ></FilterButton>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 py-3 md:py-5">
          {types.map((t, i) => (
            <div
              key={i}
              className="flex items-center rounded-full bg-neutral-100 px-3"
            >
              <Text.Body
                as="p"
                className="w-max text-neutral-900"
                key={t}
                size="lg"
              >
                &nbsp;
                {t}&nbsp;
              </Text.Body>
              &nbsp;
              <XIcon
                className="ml-2 flex h-4 w-4 cursor-pointer rounded-full hover:bg-black hover:text-white"
                onClick={() => setTypes(types.filter((type) => type !== t))}
              ></XIcon>
            </div>
          ))}
          {states.map((t, i) => (
            <div
              key={i}
              className="flex items-center rounded-full bg-neutral-100 px-3"
            >
              <Text.Body
                as="p"
                className="w-max text-neutral-900"
                key={t}
                size="lg"
              >
                &nbsp;
                {t}&nbsp;
              </Text.Body>
              &nbsp;
              <XIcon
                className="ml-2 flex h-4 w-4 cursor-pointer rounded-full hover:bg-black hover:text-white"
                onClick={() => setStates(states.filter((state) => state !== t))}
              ></XIcon>
            </div>
          ))}

          {(types.length !== 0 || states.length !== 0) && (
            <Text.Link
              className="font-extrabold "
              onClick={clearFilters}
              label={localeTexts[locale].calendar.clearFilters}
            ></Text.Link>
          )}
        </div>

        <div className="gap-4 space-y-5 md:grid md:grid-cols-3 md:space-y-0 lg:grid-cols-4">
          {allEvents.map((event) => {
            if (event.cardType === "event") {
              return (
                <EventCard
                  key={event.id}
                  size={width < breakpoints.md ? "lg" : "md"}
                  event_type={event.attributes.event_type}
                  event_title={event.attributes.event_name}
                  date_time={event.attributes.date_time}
                  display_hour={event.attributes.display_hour}
                  event_venue={
                    event.attributes?.sede?.data?.attributes?.name || ""
                  }
                  event_state={
                    event?.attributes?.sede?.data?.attirbutes?.states ||
                    localeTexts[locale].calendar.noState
                  }
                  event_image_url={
                    getStrapiImage(event?.attributes?.event_image, "small")
                  }
                  event_url={`/calendario/${event.id}`}
                />
              );
            } else {
              let projection = event;
              if (projection?.attributes?.peliculas.data?.[0]) {
                return (
                  <EventCard
                    key={projection.id}
                    size={width < breakpoints.md ? "lg" : "md"}
                    event_type={
                      projection.type === "live"
                        ? localeTexts[locale].calendar.projection
                        : projection.type === "online"
                          ? localeTexts[locale].calendar.onlineTour
                          : localeTexts[locale].calendar.tvScreening
                    }
                    event_title={projection.attributes.title}
                    date_time={
                      projection?.type === "live"
                        ? projection.attributes?.live_screenings?.[0]?.date
                        : projection?.type === "online"
                        ? {
                            from: projection?.attributes?.online_screenings?.[0]
                              ?.start_date,
                            to: projection?.attributes?.online_screenings?.[0]
                              ?.end_date,
                          }
                        : {
                            from: projection?.attributes?.tv_screenings?.[0]
                              ?.date,
                            to: projection?.attributes?.tv_screenings?.[0]
                              ?.end_date,
                          }
                    }
                    display_hour={
                      projection?.type === "live"
                        ? projection.attributes?.live_screenings?.[0]
                            ?.display_hour
                        : ""
                    }
                    event_venue={
                      projection.attributes?.sede?.data?.attributes?.name || ""
                    }
                    event_state={
                      projection?.attributes?.sede?.data?.attirbutes?.states ||
                      localeTexts[locale].calendar.noState
                    }
                    event_image_url={
                      getStrapiImage(
                        projection?.attributes?.peliculas.data?.[0]?.attributes
                          ?.film_cover_image,
                        "small"
                      )
                    }
                    event_url={`/catalogo/${projection?.attributes?.peliculas.data?.[0].attributes?.filmSlug}`}
                  />
                );
              }
            }
          })}
        </div>
      </div>

      <div className="amb-container my-10 text-center lg:hidden">
        <Text.Link
          onClick={() =>
            navRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "nearest",
            })
          }
          label={localeTexts[locale].calendar.backToTop}
          trailingIcon={<ArrowUpIcon />}
          size="lg"
          className="m-auto text-center"
        >
          &nbsp;
        </Text.Link>
      </div>
    </div>
  );
}

function FilterButton({ types, states, setTypes, setStates, locale, mexicanStates, eventTypes }) {
  const windowSize = useWindowSize();
  const [showFilters, toggleFilters] = useState(false);

  function updateStates(e) {
    if (e.target.checked) {
      setStates([...states, e.target.value]);
    } else {
      setStates(states.filter((state) => state !== e.target.value));
    }
  }

  function updateTypes(e) {
    if (e.target.checked) {
      setTypes([...types, e.target.value]);
    } else {
      setTypes(types.filter((type) => type !== e.target.value));
    }
  }

  function clearFilters() {
    setTypes([]);
    setStates([]);
  }

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Button
        onClick={() => toggleFilters(true)}
        color="secondary-dark"
        label={windowSize.width < breakpoints.md ? "" : localeTexts[locale].calendar.filters}
        trailingIcon={<AdjustmentsIcon />}
      />

      <Dialog
        open={showFilters}
        as="div"
        className="relative top-0 z-50 focus:outline-none"
        onClose={() => toggleFilters(false)}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="fixed left-0 top-0 z-50 h-screen w-full overflow-scroll rounded-t-lg bg-white shadow-md">
              <div className="amb-container bg-neutral-transparent/30 flex py-3">
                <div className="flex-1">
                  <Text.Body className="py-5 font-semibold " size="xl">
                    {localeTexts[locale].calendar.filters} <FilterIcon className="inline w-5 h-5" />
                  </Text.Body>
                </div>

                <div className="flex-grow"></div>
                <div className="flex-1 text-right">
                  <XIcon
                    className="inline h-5 w-5"
                    onClick={() => toggleFilters(false)}
                  />
                </div>
              </div>
              <div className="amb-container bg-neutral-50">
                <Text.Body as="p" className="py-2 font-semibold " size="lg">
                  {localeTexts[locale].calendar.states}
                </Text.Body>
              </div>
              <div className="amb-container my-3 max-h-[300px] w-full grid-cols-2 overflow-scroll  md:grid">
                {mexicanStates.map((t, i) => (
                  <div
                    className="flex items-center gap-4 sm:px-4 sm:py-1"
                    key={t.id}
                  >
                    <input
                      onChange={updateStates}
                      className="h-4 w-4 accent-black"
                      id={t.name}
                      value={t.name}
                      checked={states.includes(t.name)}
                      type="checkbox"
                    />
                    <label
                      className="w-full border-b-[1px] border-b-neutral-200 text-sm  text-neutral-1000 sm:py-2"
                      htmlFor={t.id}
                    >
                      {t.name}
                    </label>
                  </div>
                ))}
              </div>

              <div className="amb-container bg-neutral-50">
                <Text.Body as="p" className="py-2 font-semibold " size="lg">
                  {localeTexts[locale].calendar.eventType}
                </Text.Body>
              </div>
              <div className="amb-container mb-[200px] grid w-full grid-cols-2 ">
                {eventTypes.map((t, i) => (
                  <div
                    className="flex items-center gap-4 sm:px-4 sm:py-1"
                    key={t}
                  >
                    <input
                      onChange={updateTypes}
                      className="h-4 w-4 accent-black"
                      id={t}
                      name={i}
                      value={t}
                      checked={types.includes(t)}
                      type="checkbox"
                    />
                    <label
                      className="w-full border-b-[1px] border-b-neutral-200 text-sm  text-neutral-1000 sm:py-2"
                      htmlFor={i}
                    >
                      {t}
                    </label>
                  </div>
                ))}
              </div>

              <div className="fixed bottom-0 left-0 z-50 w-full bg-white">
                <div className="amb-container grid grid-cols-2 border-t-2 py-2 shadow-md">
                  <Text.Link
                    onClick={clearFilters}
                    label={localeTexts[locale].calendar.clearFilters}
                  ></Text.Link>
                  <Button
                    onClick={() => toggleFilters(false)}
                    color="secondary-light"
                    label={localeTexts[locale].calendar.searchEvents}
                  ></Button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export async function getServerSideProps({ locale }) {
  return {
    props: {
      locale: locale || 'es-MX',
    },
  };
}